import { Inject, Injectable } from '@angular/core';
import {
  Registrant,
  RegistrantKeys,
  RegistrantModelKeys,
  ChangeSourceType,
  Conference,
  ConferenceKeys,
  ConferencePossibleStayPeriodKey,
  HotelReservationKeys,
  HotelReservation,
} from '@ag-common-lib/public-api';
import { FirebaseApp } from 'firebase/app';
import { FIREBASE_APP } from '../../injections/firebase-app';
import { map, Observable, of } from 'rxjs';
import { CONFERENCES_COLLECTION_PATH } from '../conference.service';
import { ToastrService } from 'ngx-toastr';
import {
  InviteeStatus,
  RegistrantData,
} from '../../../../../../../ag-common-lib/projects/ag-common-lib/src/lib/models/registration/registrants.model';
import { conferenceRegistrantFromFirestore } from './conference-registrants-utils';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { CommonFireStoreDao, QueryParam, WhereFilterOperandKeys } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';
import { ChairmanExtensionConfigurationKeys } from '@ag-common-lib/lib/models/registration/conference-chairman-extension';
import { RegistrantChairmanExtensionKeys } from '@ag-common-lib/lib/models/registration/registrant-chairman-extension';

export const CONFERENCE_REGISTRANTS_COLLECTION_PATH = 'registrants';

@Injectable({
  providedIn: 'root',
})
export class ConferenceRegistrantsService {
  public readonly fsDao: CommonFireStoreDao<Registrant>;
  private readonly conferenceCollectionPath = CONFERENCES_COLLECTION_PATH;
  private readonly registrantsCollectionPath = CONFERENCE_REGISTRANTS_COLLECTION_PATH;

  constructor(
    @Inject(FIREBASE_APP) fireBaseApp: FirebaseApp,
    private toastrService: ToastrService,
  ) {
    this.fsDao = new CommonFireStoreDao<Registrant>(fireBaseApp, ConferenceRegistrantsService.fromFirestore, null);
  }

  static readonly fromFirestore = conferenceRegistrantFromFirestore;

  getDocumentData(conferenceDbId: string, registrantDbId: string): Observable<Registrant> {
    const table = ConferenceRegistrantsService.getCollectionPath(conferenceDbId);

    return this.fsDao.getDocument(table, registrantDbId).pipe(
      map(snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          return data;
        }
        return null;
      }),
    );
  }

  public getList(conferenceDbId: string, qp: QueryParam[] = []) {
    const table = ConferenceRegistrantsService.getCollectionPath(conferenceDbId);

    return this.getCollectionGroupSnapshot(table, qp);
  }

  async create(conferenceDbId: string, agentDbId: string, data: Registrant, action: ChangeSourceType, silent = false) {
    const table = ConferenceRegistrantsService.getCollectionPath(conferenceDbId);

    Object.assign(data, { [RegistrantModelKeys.conferenceDbId]: conferenceDbId, [BaseModelKeys.actionName]: action });

    const registrant = await this.fsDao.createWithId(data, agentDbId, table).catch(e => {
      // TODO add error toast
      console.log('e', e);
      throw new Error(e);
    });

    !silent && this.toastrService.success('Conference Registration Successfully Created!');

    return registrant;
  }

  async update(
    conferenceDbId: string,
    documentId: any,
    updates: Partial<Registrant>,
    action: ChangeSourceType,
    silent = false,
  ) {
    Object.assign(updates, {
      [BaseModelKeys.actionName]: action,
    });
    const table = ConferenceRegistrantsService.getCollectionPath(conferenceDbId);
    const registrant = await this.fsDao.updateFields(updates, documentId, table).catch(e => {
      // TODO add error toast
      console.log('e', e);
      throw new Error(e);
    });

    !silent && this.toastrService.success('Conference Registration Successfully Updated!');

    return registrant;
  }

  async delete(conferenceDbId: string, documentId: any, silent = false) {
    const table = ConferenceRegistrantsService.getCollectionPath(conferenceDbId);
    // TODO delete guests sub-collection
    await this.fsDao.delete(documentId, table).catch(e => {
      // TODO add error toast
      console.log('e', e);
      throw new Error(e);
    });

    !silent && this.toastrService.success('Conference Registration Successfully Updated!');
  }

  getRegistrationsByConferencesIdsAndRegistrantDbId(
    conferenceDbIds: string[],
    registrantDbId: string,
    ignoreSkipped = false,
  ) {
    const queries: QueryParam[] = [];
    conferenceDbIds?.length &&
      queries.push(new QueryParam(RegistrantModelKeys.conferenceDbId, WhereFilterOperandKeys.in, conferenceDbIds));
    queries.push(new QueryParam(BaseModelKeys.dbId, WhereFilterOperandKeys.equal, registrantDbId));

    if (!conferenceDbIds?.length && !registrantDbId) {
      return of(null);
    }

    return this.getCollectionGroupSnapshot(this.registrantsCollectionPath, queries).pipe(
      map(registrants =>
        registrants?.filter(registrant => {
          const data = registrant[RegistrantModelKeys.data];

          if (!ignoreSkipped) {
            return true;
          }
          return !(data?.[RegistrantKeys.inviteeStatus] === InviteeStatus.declined);
        }),
      ),
    );
  }

  getRegistrationsByAgentDbId(agentDbId: string, sortField?: string): Observable<Registrant[]> {
    const queries: QueryParam[] = [];
    queries.push(new QueryParam(BaseModelKeys.dbId, WhereFilterOperandKeys.equal, agentDbId));

    return this.getCollectionGroupSnapshot(this.registrantsCollectionPath, queries);
  }

  /**
   * @param ignoreCachedSnapshots drop cache-only snapshots and emit server-confirmed data only.
   *   Firestore is configured with persistentLocalCache, so a freshly attached listener first
   *   emits whatever this browser's IndexedDB happens to hold for the query — which can be a
   *   subset of the collection. A list UI that renders that subset shows a partial roster as if
   *   it were the whole one. Local writes still come through (they are pending, not cached), but
   *   while fully offline this emits nothing instead of emitting a stale partial list.
   */
  getRegistrantsByConferenceId(
    conferenceId: string,
    sortField?: string,
    qp: QueryParam[] = [],
    ignoreCachedSnapshots = false,
  ): Observable<Registrant[]> {
    qp.push(new QueryParam(RegistrantKeys.conferenceDbId, WhereFilterOperandKeys.equal, conferenceId));

    const table = ConferenceRegistrantsService.getCollectionPath(conferenceId);

    return this.fsDao.getList(table, qp, { sortField, ignoreCash: ignoreCachedSnapshots });
  }

  getRegistrant(conferenceDbId: string, registrantId: string): Observable<Registrant[]> {
    let qp: QueryParam[] = [];
    qp.push(new QueryParam(RegistrantKeys.conferenceDbId, WhereFilterOperandKeys.equal, conferenceDbId));
    qp.push(new QueryParam(BaseModelKeys.dbId, WhereFilterOperandKeys.equal, registrantId));

    return this.getCollectionGroupSnapshot(this.registrantsCollectionPath, qp);
  }

  static getCollectionPath(conferenceId: string): string {
    return [CONFERENCES_COLLECTION_PATH, conferenceId, CONFERENCE_REGISTRANTS_COLLECTION_PATH].join('/');
  }

  getCollectionGroupSnapshot(table, queries: QueryParam[] = []) {
    return this.fsDao.getCollectionGroupSnapshot(table, queries, true).pipe(
      map(snapshot =>
        snapshot.docs?.map(doc => {
          if (!doc.exists()) {
            return null;
          }

          const data = doc.data();

          return data;
        }),
      ),
    );
  }

  static getRegistrantTravelDates(registrantData: RegistrantData, conference: Conference): Partial<HotelReservation> {
    const registrationType = registrantData?.[RegistrantKeys.registrationType];
    const isApplicableForChairmanExtension = registrantData?.[RegistrantKeys.isApplicableForChairmanExtension];
    const chairmanExtensionConfiguration = registrantData?.[RegistrantKeys.chairmanExtension];
    const chairmanExtensionInviteeStatus =
      chairmanExtensionConfiguration?.[RegistrantChairmanExtensionKeys.inviteeStatus];
    const isChairmanExtensionEnabled = conference?.[ConferenceKeys.isChairmanExtensionEnabled];
    const participantScheduleRanges = conference?.[ConferenceKeys.participantScheduleRanges];
    const participantScheduleRangesForExtension =
      conference?.[ConferenceKeys.chairmanExtensionConfiguration]?.[ChairmanExtensionConfigurationKeys.stayPeriods];
    const isExtensionApplicable =
      isChairmanExtensionEnabled &&
      isApplicableForChairmanExtension &&
      chairmanExtensionInviteeStatus !== InviteeStatus.declined;
    const stayPeriods = isExtensionApplicable ? participantScheduleRangesForExtension : participantScheduleRanges;
    const scheduleRange = stayPeriods.find(
      range => range?.[ConferencePossibleStayPeriodKey.registrationType] === registrationType,
    );
    const arrivalDate =
      scheduleRange?.[ConferencePossibleStayPeriodKey.primaryArrivalDate] ?? conference?.[ConferenceKeys.startDate];
    const departureDate =
      scheduleRange?.[ConferencePossibleStayPeriodKey.primaryDepartureDate] ?? conference?.[ConferenceKeys.endDate];

    return {
      [HotelReservationKeys.checkInDate]: arrivalDate,
      [HotelReservationKeys.checkOutDate]: departureDate,
      [HotelReservationKeys.requestedCheckInDate]: arrivalDate,
      [HotelReservationKeys.requestedCheckOutDate]: departureDate,
    };
  }
}
